"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeVideos = exports.processVideoGallery = void 0;
/**
 * Maximum number of imported videos retained in a gallery.  Regular videos
 * and shorts are capped independently.
 */
const MAX_IMPORTED = 25;
/** How many videos smart display picks for the page. */
const SMART_DISPLAY_LIMIT = 12;
/**
 * `publishedAt` is typed as a client SDK `Timestamp`, but these documents are
 * also read and written by the admin SDK in cloud functions, where that is a
 * different class.  Read the value structurally so ordering works in both.
 */
const toMillis = (media) => {
    const publishedAt = media.publishedAt;
    return typeof (publishedAt === null || publishedAt === void 0 ? void 0 : publishedAt.toMillis) === "function"
        ? publishedAt.toMillis()
        : 0;
};
/** Most recently published first.  Videos with no date sort to the end. */
const byPublishedAt = (a, b) => toMillis(b) - toMillis(a);
/** Whether a video is the type this gallery displays. */
const matchesMode = (video, mode) => mode === "videos-and-shorts" || (mode === "shorts-only") === !!video.short;
/**
 * Applies smart display to a set of videos: orders them newest first, caps
 * how many are retained, and selects the ones the page shows.
 *
 * Smart display shows the {@link SMART_DISPLAY_LIMIT} most recent videos
 * matching the gallery's mode.  A pinned video is never turned off, whatever
 * its age or type, and counts towards the limit — so pinning three videos
 * means nine automatic ones, not twelve.
 *
 * With `keepSelected`, videos already showing hold their place too, and only
 * the oldest are dropped once there are more than the limit.  A gallery
 * below the limit therefore loses nothing.
 */
const processVideoGallery = ({ videos, smartDisplayMode, keepPinned = false, keepSelected = false, }) => {
    // The selection loop keys on `id`, so a repeated ID would be marked
    // selected on every copy while only ticking `shown` once — pushing the
    // total past the limit.  `mergeVideos` de-duplicates upstream, but the
    // editor's mode-change path passes stored `values.videos` here directly,
    // and legacy galleries hold duplicates from before that fix.
    const unique = new Map();
    for (const video of videos) {
        if (!unique.has(video.id)) {
            unique.set(video.id, video);
        }
    }
    const deduped = [...unique.values()];
    // Retention is per type so a burst of shorts can't push every regular
    // video out of the gallery, and vice versa.
    const normal = deduped
        .filter((v) => !v.short)
        .sort(byPublishedAt)
        .slice(0, MAX_IMPORTED);
    const shorts = deduped
        .filter((v) => v.short)
        .sort(byPublishedAt)
        .slice(0, MAX_IMPORTED);
    const media = [...normal, ...shorts];
    media.sort(byPublishedAt);
    const isPinned = (video) => keepPinned && !!video.pinned;
    const isShowing = (video) => keepSelected && video.selected !== false;
    const matches = (video) => matchesMode(video, smartDisplayMode);
    const chosen = new Set();
    let shown = 0;
    // A pinned video is kept whatever its age or type, and takes one of the
    // places rather than sitting on top of them.
    for (const video of media) {
        if (isPinned(video)) {
            chosen.add(video.id);
            shown += 1;
        }
    }
    // A gallery already at the limit makes room for newer videos by dropping
    // its oldest, so above it selection interleaves showing and matching in
    // newest-first order.
    //
    // Below it, showing videos take places before newer matching ones — even
    // when that means passing over videos that would otherwise be selected on
    // recency alone.  A page displaying ten videos with ten newer uploads
    // arriving keeps its ten and tops up with two of the new, not the twelve
    // newest.  That's the explicit "under 12 we don't uncheck any that are
    // checked" rule this refresh was built around.
    const atLimit = media.filter((video) => isPinned(video) || isShowing(video)).length >=
        SMART_DISPLAY_LIMIT;
    const passes = atLimit
        ? [(video) => isShowing(video) || matches(video)]
        : [isShowing, matches];
    for (const accept of passes) {
        for (const video of media) {
            if (shown >= SMART_DISPLAY_LIMIT) {
                break;
            }
            if (chosen.has(video.id) || !accept(video)) {
                continue;
            }
            chosen.add(video.id);
            shown += 1;
        }
    }
    return media.map((video) => ({ ...video, selected: chosen.has(video.id) }));
};
exports.processVideoGallery = processVideoGallery;
/**
 * Combines the videos already on a gallery with a set fetched from the
 * artist's channel, keyed on video ID.
 *
 * YouTube wins for metadata (a title or thumbnail may have changed), the
 * artist wins for the choices they made in the editor (`pinned`, `selected`).
 * Videos already present keep their position so a refresh doesn't reshuffle
 * the gallery; videos we haven't seen before lead, since they're the newest.
 *
 * Both inputs are de-duplicated, so this also repairs galleries that already
 * contain the same video more than once.
 */
const mergeVideos = (existing, incoming) => {
    var _a, _b, _c;
    const incomingById = new Map();
    for (const video of incoming) {
        if (!incomingById.has(video.id)) {
            incomingById.set(video.id, video);
        }
    }
    const seen = new Set();
    const kept = [];
    for (const video of existing) {
        if (seen.has(video.id)) {
            continue;
        }
        seen.add(video.id);
        const update = incomingById.get(video.id);
        if (!update) {
            kept.push(video);
            continue;
        }
        kept.push({
            ...video,
            ...update,
            imported: (_a = update.imported) !== null && _a !== void 0 ? _a : video.imported,
            pinned: (_b = video.pinned) !== null && _b !== void 0 ? _b : update.pinned,
            selected: (_c = video.selected) !== null && _c !== void 0 ? _c : update.selected,
        });
    }
    const added = [];
    for (const video of incoming) {
        if (seen.has(video.id)) {
            continue;
        }
        seen.add(video.id);
        added.push(video);
    }
    return [...added, ...kept];
};
exports.mergeVideos = mergeVideos;
//# sourceMappingURL=videos.js.map